import { Button, makeStyles, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ClientConfiguration } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { updateConversationAction } from '../../store/actions/conversationActions';
import { useConversations } from '../../store/hooks';

class ModelSelectionProps {
    changeModel: (model: string) => void = () => {};
    disabled: boolean = false;
}

const useClasses = makeStyles({
    modelSelection: {
        width: '200px',
    },
});

export default function ModelSelection(props: ModelSelectionProps) {
    const { changeModel, disabled } = props;
    const [clientConfiguration, setClientConfiguration] = useState<ClientConfiguration>();
    const [menuTitle, setMenuTitle] = useState<string>('Model Selection');
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const classes = useClasses();
    const { activeChat, dispatch } = useConversations();
    const { activeGpt } = useConversations();
    const userSelectedModelasRef = useRef<string>("");

    const handleModelChange = useCallback((model: string) => {
        setMenuTitle(model);
        changeModel(model);        
        userSelectedModelasRef.current = model; // Mark that user has manually selected a model
        
        // Update the active chat with the selected model using the proper action dispatch
        if (activeChat?.id) {
            dispatch(updateConversationAction({
                id: activeChat.id,
                patch: { llmDeploymentName: model }
            }));
        }
    }, [changeModel, activeChat?.id, dispatch]);

    useEnterpriseGPTApiCall(async (client, abortSignal) => {
        try {
            setHasError(false);
            const getClientConfigurationsOptions = await client.getClientConfigurationsOptions(abortSignal);
            setClientConfiguration(getClientConfigurationsOptions);

            // Only auto-set model if user hasn't manually selected one or if the conversation is already started
            if (!userSelectedModelasRef.current || (activeChat?.prompts && activeChat.prompts.length > 0)) {
                if (activeChat?.prompts && activeChat.prompts.length > 0
                    && activeChat?.llm
                    && getClientConfigurationsOptions?.llmModels?.includes(activeChat.llm)) {
                    setMenuTitle(activeChat.llm);
                    changeModel(activeChat.llm);
                } else if (getClientConfigurationsOptions?.llmModels && getClientConfigurationsOptions.llmModels.length > 0) {
                    setMenuTitle(getClientConfigurationsOptions.llmModels[0]);
                    changeModel(getClientConfigurationsOptions.llmModels[0]);
                }
            }
            setIsLoading(false);
        } catch (e: unknown) {
            if ((e as Error).message !== 'canceled') {
                console.error('Error fetching client configurations options:', e);
                setHasError(true);
                // Create a fallback configuration to maintain UI consistency
                const fallbackConfig = new ClientConfiguration();
                fallbackConfig.llmModels = ['Default Model'];
                setClientConfiguration(fallbackConfig);
                setMenuTitle('Model Selection');
            }
            setIsLoading(false);
        }
    }, [changeModel]);

    useEffect(() => {
        // Only restore the model from activeChat when switching to a different chat
        // and that chat has a model already set, and user hasn't manually selected a model
        if ( activeChat && activeChat.llm && activeChat.llm != '') {
            setMenuTitle(activeChat.llm);
            changeModel(activeChat.llm);            
        } else if (userSelectedModelasRef.current != "") {
            // Reset when no active chat
            setMenuTitle(userSelectedModelasRef.current);
            changeModel(userSelectedModelasRef.current);
        }
    }, [activeChat, changeModel, dispatch]);

    // Don't render if there are less than 2 models available (and no error state)
    if (!hasError && clientConfiguration?.llmModels && clientConfiguration.llmModels.length < 2) {
        return null;
    }

    return (
        <div className={classes.modelSelection}>
            {!isLoading && (
                <Menu>
                    <MenuTrigger disableButtonEnhancement>
                        <Button disabled={disabled || hasError}>{menuTitle}</Button>
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            {clientConfiguration?.llmModels?.map((model) => (
                                <MenuItem key={model} onClick={() => handleModelChange(model)}>
                                    {model}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </MenuPopover>
                </Menu>
            )}
            {isLoading && (
                <Button disabled={true}>Loading...</Button>
            )}
        </div>
    );
}
